import { init as initPell } from 'pell/dist/pell.js';
import { prepareRichTextInput, sanitizeRichText } from '../../services/rich-text';
import { t } from '../../services/i18n';

type PellAction = {
  icon: string;
  title: string;
  result: () => boolean | void;
};

export class RcRichTextEditor extends HTMLElement {
  private hiddenInput: HTMLInputElement | null = null;
  private editorContent: HTMLDivElement | null = null;
  private editorRoot: HTMLDivElement | null = null;
  private initialized = false;

  connectedCallback(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.render();
  }

  get value(): string {
    return this.hiddenInput?.value || '';
  }

  set value(nextValue: string) {
    const prepared = prepareRichTextInput(nextValue);
    if (this.editorContent) {
      this.editorContent.innerHTML = prepared;
      this.updateEmptyState();
    }
    if (this.hiddenInput) {
      this.hiddenInput.value = sanitizeRichText(prepared);
    }
  }

  private render(): void {
    const name = this.getAttribute('name') || '';
    const initialValue = prepareRichTextInput(this.getAttribute('value') || this.textContent || '');
    const placeholder = this.getAttribute('placeholder') || t('common.notesPlaceholder');

    this.innerHTML = `
      <div class="rich-text-editor"></div>
      <input type="hidden" ${name ? `name="${name}"` : ''}>
    `;

    this.hiddenInput = this.querySelector('input');
    this.editorRoot = this.querySelector('.rich-text-editor');
    if (!this.editorRoot || !this.hiddenInput) return;

    const actions: PellAction[] = [
      {
        icon: '<strong>B</strong>',
        title: t('richText.bold'),
        result: () => document.execCommand('bold', false),
      },
      {
        icon: '&#8226;',
        title: t('richText.bulletList'),
        result: () => document.execCommand('insertUnorderedList', false),
      },
      {
        icon: '1.',
        title: t('richText.numberedList'),
        result: () => document.execCommand('insertOrderedList', false),
      },
      {
        icon: '&#128279;',
        title: t('richText.link'),
        result: () => {
          const url = window.prompt(t('richText.linkPrompt'));
          if (!url) return false;
          return document.execCommand('createLink', false, url.trim());
        },
      },
    ];

    const editor = initPell({
      element: this.editorRoot,
      actions,
      defaultParagraphSeparator: 'p',
      onChange: (html: string) => {
        if (!this.hiddenInput) return;
        this.hiddenInput.value = sanitizeRichText(html);
        this.updateEmptyState();
      },
    }) as HTMLDivElement & { content?: HTMLDivElement };

    this.editorContent = editor.content || this.editorRoot.querySelector('.pell-content');
    if (!this.editorContent) return;

    this.editorContent.dataset.placeholder = placeholder;
    this.editorContent.innerHTML = initialValue;
    this.hiddenInput.value = sanitizeRichText(initialValue);

    this.editorContent.addEventListener('paste', (event) => {
      event.preventDefault();
      const text = event.clipboardData?.getData('text/plain') || '';
      document.execCommand('insertText', false, text);
    });

    this.updateEmptyState();
  }

  private updateEmptyState(): void {
    if (!this.editorContent) return;
    const sanitized = sanitizeRichText(this.editorContent.innerHTML);
    this.editorContent.classList.toggle('is-empty', !sanitized);
  }
}

customElements.define('rc-rich-text-editor', RcRichTextEditor);
